import {
  AloftArea,
  AloftPrice,
  CraftsmanCostProps,
  ORDER_FEATURES,
  getCraftsmanCost,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectImprovementEntityIds,
  selectImprovementsEntities,
} from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculateDeckCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const identification = propertyIdentification[id];

  if (!improvementsForTransaction || !identification) return undefined;

  const zipCode = identification.address.PostalCode;
  const { deck, craftsmanQuality, propertyType } = improvementsForTransaction;
  if (isUndefined(craftsmanQuality) || isUndefined(zipCode) || !deck || !propertyType) {
    return undefined;
  }

  const calculator = getCraftsmanCost(costProps);

  const params = deck.area
    ? {
        hasFeature: ORDER_FEATURES.deck.hasFeature(improvementsForTransaction),
        propertyType,
        type: deck.type,
        quality: deck.quality ?? craftsmanQuality,
        sqft: AloftArea.fromNumber(deck.area.unitAmount, deck.area.unit),
        zipCode,
      }
    : {
        hasFeature: ORDER_FEATURES.deck.hasFeature(improvementsForTransaction),
        propertyType,
        type: deck.type,
        quality: deck.quality ?? craftsmanQuality,
        zipCode,
      };

  return calculator.getDeckCost(params);
};

const getTotalOrBreakdownDeckCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
  type: 'totalCost' | 'zipOnly',
) => {
  const costOutput = calculateDeckCost(costProps, improvements, propertyIdentification, id);

  if (!costOutput) return AloftPrice.zero;
  return type === 'totalCost' ? costOutput.totalCost : costOutput.multiplierBreakdown[type];
};

const getTotalDeckCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => getTotalOrBreakdownDeckCost(costProps, improvements, propertyIdentification, id, 'totalCost');

export const selectDeckCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  getTotalDeckCost,
);

export const selectZipOnlyDeckCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  (costProps, improvements, propertyIdentification, id) =>
    getTotalOrBreakdownDeckCost(costProps, improvements, propertyIdentification, id, 'zipOnly'),
);

export const selectDeckCosts = createSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectImprovementEntityIds,
  ],
  (costProps, improvements, propertyIdentification, improvementEntityIds) =>
    improvementEntityIds.reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = getTotalDeckCost(costProps, improvements, propertyIdentification, propID);
      if (isDefined(cost)) acc[propID] = cost;
      return acc;
    }, {}),
);

export const selectDeckCostDisplay = createSelector([selectDeckCost], (deckCost) =>
  deckCost?.toString(true),
);
